import styled from 'styled-components';
import { Button } from 'antd';

/*
 * DWEB-172 (v6.8.0): the tint comes from --glass-bg / --glass-border /
 * --glass-bg-hover / --glass-border-hover, declared once in src/index.css.
 * This file states none of the four values.
 *
 * 🔴 THE `!important`s ON THOSE FOUR DECLARATIONS ARE LOAD-BEARING, AND THAT
 * IS MEASURED. v6.8.0's fence permitted removing them; they were removed,
 * the build was measured, and the release gate went RED — so they went back.
 * Read this before deleting one:
 *
 *   REST is a source-order win only. antd's competitor is
 *   `:where(.css-…).ant-btn-variant-outlined { background:#ffffff }`. `:where()`
 *   contributes ZERO specificity, so that rule and this component's class are
 *   BOTH (0,1,0); the tint wins because styled-components injects after antd's
 *   cssinjs, which is an emergent property of two style runtimes, not a
 *   contract. Removing `!important` here measured CORRECT — today.
 *
 *   HOVER LOSES OUTRIGHT. antd's
 *   `:where(.css-…).ant-btn-variant-outlined:not(:disabled):not(.ant-btn-disabled):hover`
 *   scores (0,4,0) — the two `:not()`s each count — against `&:hover`'s
 *   (0,2,0). With the keyword gone, every button on this site turned WHITE on
 *   hover: measured rgb(255,255,255) / border rgb(64,150,255), which are
 *   antd's #ffffff and #4096ff exactly.
 *
 * The lesson is DWEB-25's, one layer down: taking a declaration out of the
 * `!important` origin drops it back into a specificity contest it may lose,
 * and "the rest state still looks right" is not evidence about the hover
 * state. Measure the state you are changing.
 *
 * e2e/glass-tint.spec.js asserts the computed background and border of a real
 * button in BOTH rest and hover, so the source-order dependency that protects
 * the rest state is now a red build rather than a silent redesign.
 *
 * The other 22 `!important`s in this file are untouched: v6.8.0's fence
 * covered only the declarations carrying the tint. They are not endorsed —
 * they are out of scope, and counted in the release record.
 */

const LiquidGlassButton = styled(Button)`
  /* Liquid glass button with dark variant default */
  background: var(--glass-bg) !important;
  
  backdrop-filter: blur(${props => props.blur || '12px'}) !important;
  -webkit-backdrop-filter: blur(${props => props.blur || '12px'}) !important;
  
  border: 1px solid var(--glass-border) !important;
  
  color: white !important;
  text-shadow: 0 0 15px rgba(30, 64, 175, 0.8), 0 0 30px rgba(30, 64, 175, 0.4) !important;
  
  /* Squircle border radius */
  border-radius: ${props => props.radius || '12px'} !important;
  ${props => props.squircle && `
    clip-path: polygon(
      ${props.radius || '12px'} 0%, calc(100% - ${props.radius || '12px'}) 0%, 
      100% 0%, 100% ${props.radius || '12px'}, 
      100% calc(100% - ${props.radius || '12px'}), 100% 100%, 
      calc(100% - ${props.radius || '12px'}) 100%, ${props.radius || '12px'} 100%, 
      0% 100%, 0% calc(100% - ${props.radius || '12px'}), 
      0% ${props.radius || '12px'}, 0% 0%
    ) !important;
  `}
  
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif !important;
  font-weight: ${props => props.fontWeight || '400'} !important;
  
  box-shadow: ${props => {
    const { theme, shadowIntensity = 0.2, glassVariant = 'auto' } = props;
    const isDark = glassVariant === 'dark' || (glassVariant === 'auto' && theme?.isDarkMode);
    return `inset 0 1px 0 rgba(255, 255, 255, ${isDark ? '0.1' : '0.8'}), 0 4px 12px -2px rgba(0, 0, 0, ${shadowIntensity})`;
  }} !important;
  
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
  
  &:hover, &:focus {
    color: white !important;
    text-shadow: 0 0 20px rgba(30, 64, 175, 1), 0 0 40px rgba(30, 64, 175, 0.6) !important;
    
    background: var(--glass-bg-hover) !important;
    
    border-color: var(--glass-border-hover) !important;
    
    transform: translateY(-2px) scale(1.05) !important;
    
    box-shadow: ${props => {
      const { theme, shadowIntensity = 0.2 } = props;
      return `inset 0 1px 0 rgba(255, 255, 255, ${theme?.isDarkMode ? '0.15' : '0.9'}), 0 8px 24px -4px rgba(0, 0, 0, ${shadowIntensity + 0.1})`;
    }} !important;
    
    backdrop-filter: blur(${props => parseInt(props.blur || '12') + 4}px) !important;
    -webkit-backdrop-filter: blur(${props => parseInt(props.blur || '12') + 4}px) !important;
  }
  
  /* Size variants */
  ${props => props.size === 'small' && `
    padding: 6px 12px !important;
    font-size: 12px !important;
    height: auto !important;
  `}
  
  ${props => props.size === 'large' && `
    padding: 12px 24px !important;
    font-size: 16px !important;
    height: auto !important;
  `}
`;

export default LiquidGlassButton;
